// src/encounter/lib/rail-note-landing.ts
// The page side of the rail's "Add to note" (left-rail target 4 Nov §7.2): WHICH note line a landing is, and whether it can take a
// piece now. Pure; the page (encounter-shell-page.tsx) passes its own facts in and writes through its own answer + Delta setters.
//
//   · HPI → the HPI section's narrative line, ROS → the ROS section's narrative line: the section's `-narrative` item
//     (`findNarrativeItem` on `questionnaireForStep`, the same item the structured note draws as that block's note line and the
//     `hpi-narrative` / `ros-narrative` phrase editors write). No such item in this template → the landing is `absent`.
//   · Assessment → always `absent`: the Assessment free-text box does not exist yet (§4d), and the A&P narrative is never used in
//     its place (§7.2).
//   · Only the structured note (frame v2) keeps a line's pieces (`cps-note-delta`); elsewhere a piece would be saved as bare text,
//     no longer attributable, so the landing is `absent` there.
import type { Questionnaire } from '@scripsteam/scrips-signal-ds';
import { findNarrativeItem, questionnaireForStep } from './questionnaire-groups';
import {
  ALREADY_IN_NOTE,
  LANDING_ABSENT,
  LANDING_READY,
  NO_NOTE_REASON,
  landingBlocked,
  lineHasPieceFrom,
  type RailLandingState,
  type RailNoteLanding,
} from './rail-note-piece';

/** The ruled wording when the note is signed (left-rail target §4b). */
export const NOTE_SIGNED = 'Note signed';
/** The note has not loaded (or could not be read), so nothing can be added to it yet. */
export const NOTE_NOT_READY = 'The note is still loading. Try again in a moment.';

/** The linkId of the note line a landing writes to, or undefined when the template has none. */
export function railLandingLinkId(questionnaire: Questionnaire | undefined, landing: RailNoteLanding): string | undefined {
  if (!questionnaire || landing === 'assessment') return undefined;
  return findNarrativeItem(questionnaireForStep(questionnaire, landing).item)?.linkId;
}

export interface RailLandingFacts {
  /** The visit has started (there is an encounter note to write). */
  started: boolean;
  /** The structured note keeps line pieces (frame v2). */
  linePieces: boolean;
  /** The steps this user's note shows (`navSteps`): a landing whose step is not there is absent. */
  steps: readonly string[];
  /** The landing's line, from `railLandingLinkId`. */
  linkId: string | undefined;
  /** The provider has signed (or this page's sign just went through), or the server refused a write as signed. */
  signed: boolean;
  /** The saved note has loaded for this visit and can be saved (the page's `findingsWritable`, which also covers `signed`). */
  writable: boolean;
  /** The line's stored text and Delta now, for the duplicate check. */
  value?: unknown;
  delta?: string;
}

/** The landing's state for a record (`sourceId`), from the page's facts (rules in the file header). */
export function railLandingState(landing: RailNoteLanding, facts: RailLandingFacts, sourceId?: string): RailLandingState {
  if (landing === 'assessment') return LANDING_ABSENT;
  if (!facts.started) return landingBlocked(NO_NOTE_REASON);
  if (!facts.linePieces || !facts.steps.includes(landing) || !facts.linkId) return LANDING_ABSENT;
  if (facts.signed) return landingBlocked(NOTE_SIGNED);
  if (!facts.writable) return landingBlocked(NOTE_NOT_READY);
  if (sourceId && lineHasPieceFrom(facts.delta, facts.value, sourceId)) return landingBlocked(ALREADY_IN_NOTE);
  return LANDING_READY;
}
